import { Link } from "react-router-dom";
import { Phone, Mail, Linkedin, Facebook, Instagram, Twitter } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white relative noise-overlay" data-testid="footer">
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
                <span className="font-bold text-slate-900 text-xl font-['Barlow_Condensed']">BE</span>
              </div>
              <span className="font-bold text-white text-lg font-['Barlow_Condensed'] tracking-tight">
                BUILDEDGE TECH
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Advanced GFRP solutions for modern infrastructure. Building stronger, lighter, and smarter structures.
            </p>
            <div className="accent-line"></div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-white mb-6 font-['Barlow_Condensed'] text-lg uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Products", path: "/products" },
                { name: "Why GFRP", path: "/why-gfrp" },
                { name: "Applications", path: "/applications" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-yellow-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-bold text-white mb-6 font-['Barlow_Condensed'] text-lg uppercase tracking-wider">
              Our Products
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/products" 
                  className="text-slate-400 hover:text-yellow-400 transition-colors text-sm"
                >
                  PRO BAR (GFRP Rebar)
                </Link>
              </li>
              <li>
                <Link 
                  to="/products" 
                  className="text-slate-400 hover:text-yellow-400 transition-colors text-sm"
                >
                  PRO MESH (GFRP Mesh)
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-white mb-6 font-['Barlow_Condensed'] text-lg uppercase tracking-wider">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:+919871070356" 
                  className="flex items-center gap-3 text-slate-400 hover:text-yellow-400 transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  +91-9871070356
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@buildedgetech.com" 
                  className="flex items-center gap-3 text-slate-400 hover:text-yellow-400 transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  info@buildedgetech.com
                </a>
              </li>
            </ul>

            {/* Social Media Links */}
            <div className="mt-6">
              <p className="text-slate-400 text-sm mb-3">Follow Us</p>
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.linkedin.com/company/buildedge-technologies-private-limited/?viewAsMember=true" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-yellow-400 flex items-center justify-center transition-colors group"
                  data-testid="footer-linkedin"
                >
                  <Linkedin className="w-5 h-5 text-yellow-400 group-hover:text-slate-900" />
                </a>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-yellow-400 flex items-center justify-center transition-colors group"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-5 h-5 text-yellow-400 group-hover:text-slate-900" />
                </a>
                <a 
                  href="https://www.instagram.com/buildedgetech/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-yellow-400 flex items-center justify-center transition-colors group"
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-5 h-5 text-yellow-400 group-hover:text-slate-900" />
                </a>
                <a 
                  href="https://x.com/BuildEdgeTech" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-slate-800 hover:bg-yellow-400 flex items-center justify-center transition-colors group"
                  data-testid="footer-x"
                >
                  <Twitter className="w-5 h-5 text-yellow-400 group-hover:text-slate-900" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} BuildEdge Tech. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Engineering the Future of Infrastructure
          </p>
        </div>
      </div>
    </footer>
  );
}
